import {PlayerId} from '@glark-newco/game-library';
import {Draft} from 'immer';
import {toNumber} from 'lodash';
import {IPointData} from 'pixi.js';
import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {LinkRelationship} from '../SupplyChainPayloadTypes';


export type NodeId = number;
export type PositionNode = {id: NodeId} & IPointData;
export type NodeOwnership = Record<NodeId, PlayerId>;

export interface Link {
    nodeIdA: NodeId,
    nodeIdB: NodeId,
    relationship: LinkRelationship,
}

export interface NetworkState {
    nodes: Record<NodeId, PositionNode>;
    links: Link[];
    ownership: NodeOwnership;

    /* ------------------- actions -------------------------------- */
    reset: () => void;
    addNode: (payload: IPointData & {nodeId: NodeId}) => void;
    deleteNode: (nodeId: NodeId) => void;
    moveNode: (nodeId: NodeId, x: number, y: number) => void;
    getPlacedPlayerIds: () => PlayerId[];
    getPlayerByNodeId: (nodeId: NodeId) => PlayerId | undefined;
    setNodeOwner: (nodeId: NodeId, playerId?: PlayerId) => void;
    addLinkByNodeId: (a: NodeId, b: NodeId, relationship: LinkRelationship) => void;
    deleteLink: (a: NodeId, b: NodeId) => void;

    /* ------- bulk update used by the GameEvent projection ---------- */
    updateNetwork: (updater: (draft: Draft<NetworkState>) => void) => void;
}

export const networkStore = create<NetworkState>()(
    immer((set, get) => ({
        nodes: {},
        links: [],
        ownership: {},

        /* ---------- actions ---------- */
        reset :() => {
            set((state: Draft<NetworkState>) => {
                state.nodes = {};
                state.links = [];
                state.ownership = {};
            });
        },
        addNode: (payload) => {
            set((state: Draft<NetworkState>) => {
                const nodeId: NodeId = payload.nodeId ??
                    Math.max(0, ...Object.keys(state.nodes).map(k => parseInt(k))) + 1;

                state.nodes[nodeId] = {
                    id: nodeId,
                    x: payload.x,
                    y: payload.y,
                };
            });
        },

        deleteNode: (nodeId) => {
            set(state => {
                delete state.nodes[nodeId];
                delete state.ownership[nodeId];
                // also prune any links that referenced the node
                state.links = state.links.filter(
                    l => l.nodeIdA !== nodeId && l.nodeIdB !== nodeId,
                );
            });
        },

        moveNode: (nodeId, x, y) => {
            set(state => {
                const node = state.nodes[nodeId];
                if (!node) return;
                node.x = x;
                node.y = y;
            });
        },

        getPlacedPlayerIds: () => {
            return Object.values(get().ownership);
        },
        getPlayerByNodeId: (nodeId: NodeId): PlayerId | undefined => {
            return networkStore.getState().ownership[nodeId];
        },
        setNodeOwner: (nodeId: NodeId, newPlayerId?: PlayerId) => {
            set(state => {
                if (newPlayerId) {
                    Object.entries(state.ownership).forEach(([nodeIdStr, playerId]) => {
                        if (playerId === newPlayerId) {
                            delete state.ownership[toNumber(nodeIdStr)];
                        }
                    });
                    state.ownership[nodeId] = newPlayerId;
                } else {
                    delete state.ownership[nodeId];
                }
            });
        },

        addLinkByNodeId: (a, b, relationship) => {
            set(state => {
                // avoid duplicates
                const exists = state.links.some(
                    l => (l.nodeIdA === a && l.nodeIdB === b) ||
                        (l.nodeIdA === b && l.nodeIdB === a),
                );
                if (!exists) {
                    state.links.push({nodeIdA: a, nodeIdB: b, relationship});
                }
            });
        },

        deleteLink: (a, b) => {
            set(state => {
                state.links = state.links.filter(
                    l => !(l.nodeIdA === a && l.nodeIdB === b) &&
                        !(l.nodeIdA === b && l.nodeIdB === a),
                );
            });
        },

        updateNetwork: (updater) => {
            set((state) => {
                updater(state);
            });
        },
    })),
);

export const useNetworkStore = networkStore;

